import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { Badge } from 'app/shared/halo/components/ui/Badge';
import type { RentalTypeBadge } from './getRentalTypeBadges';
import { getRentalTypeBadges } from './getRentalTypeBadges';
import * as styles from './rentalTypeBadges.styles';

type RentalTypeBadgesProps = {
  // When provided, skips the internal selector. Lets parents (e.g. SummaryRow)
  // compute the badges once and reuse them for the gate decision.
  badges?: Array<RentalTypeBadge>;
};

export const RentalTypeBadges = ({ badges: badgesProp }: RentalTypeBadgesProps = {}) => {
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const badges = badgesProp ?? getRentalTypeBadges(currentListing);

  if (badges.length === 0) {
    return null;
  }

  // `role="list"` defends against Safari/VoiceOver stripping the implicit list
  // role from <ul>s with `list-style: none` (our flex container drops bullets).
  // `data-rental-type-list` is a structural hook used by `summaryRow`'s
  // `:has(> [data-rental-type-list])` selector — keep in sync if renamed.
  return (
    <ul role="list" aria-label="Rental type" data-rental-type-list="" className={styles.container}>
      {badges.map((badge) => (
        <li key={badge.key}>
          <Badge tone="primary" size="sm">
            {badge.label}
          </Badge>
        </li>
      ))}
    </ul>
  );
};
